<template>
    <ProfileShareBackNotification v-if="canPromptShareBack" :text="bannerText" :disabled="isRefreshing" data-testid="share-back-banner" @share="startShareBack" />
    <BaseNotification v-if="isReadyToShare" theme="primary" hide-dismiss data-testid="share-back-ready">
        <template #content>
            <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-6">
                <div class="flex flex-col gap-1">
                    <p class="body">{{ t("profile.collaboration.shareBack.readyTitle", { name: shareRecipientName }) }}</p>
                    <p class="body-small">{{ sharingDisclosure }}</p>
                    <p v-if="profile?.access === ACCESS.PRIVATE" class="body-small">{{ privateSharingHelp }}</p>
                </div>
                <BaseButton theme="secondary" class="shrink-0" :disabled="isRefreshing || profile?.is_flagged || !profileReady" @click="startShareBack">{{
                    t("profile.collaboration.shareBack.share")
                }}</BaseButton>
            </div>
        </template>
    </BaseNotification>
    <BaseNotification v-if="error" theme="error" :text="error" @dismiss="error = null" />

    <BaseDialog
        v-model:open="isLinkedOpen"
        :title="landingTitle"
        :width="profile?.access === ACCESS.PRIVATE ? 'default' : 'wide'"
        :mobile-height="profile?.access === ACCESS.PRIVATE ? 'standard' : 'expanded'"
        :reserve-mobile-nav-space="false"
        @update:open="onLinkedOpenChange">
        <div v-if="profile?.access === ACCESS.PRIVATE" class="flex flex-col items-center gap-4">
            <div class="mt-2 flex w-full items-center justify-center gap-3 rounded-lgr bg-surface-sunken py-10" aria-hidden="true">
                <CollaborationProfileAvatar size="lg" :name="target?.name" />
                <i class="bi bi-arrow-left-right text-ink-muted" aria-hidden="true" />
                <CollaborationProfileAvatar size="lg" :name="creation.user_basic.full_name" />
            </div>
            <div class="flex w-full flex-col">
                <p class="body-small-bold md:body-bold text-ink">{{ landingHeadline }}</p>
                <div class="flex flex-col gap-3">
                    <p class="body-small text-ink-muted">{{ t("profile.collaboration.shareBack.landing.privateLead", { name: targetFirstName }) }}</p>
                    <p class="body-small text-ink-muted">{{ t("profile.collaboration.shareBack.landing.privateDisclosure") }}</p>
                </div>
            </div>
        </div>
        <div v-else class="flex flex-col gap-4">
            <p class="body-small md:body text-ink-muted">
                <span class="body-small-bold md:body-bold block text-ink">{{ landingHeadline }}</span>
                {{ landingDescription }}
            </p>
            <ProfileShareBackComparison :sender-name="target?.name ?? ''" :viewer-name="creation.user_basic.full_name" />
        </div>
        <template #footer>
            <template v-if="profile?.access === ACCESS.PRIVATE">
                <BaseButton theme="neutral" @click="dismissLinked">{{ t("profile.collaboration.shareBack.maybeLater") }}</BaseButton>
                <BaseButton theme="primary" :disabled="isRefreshing" @click="chooseShareBack">{{ t("profile.collaboration.shareBack.enableSharing") }}</BaseButton>
            </template>
            <BaseButton v-else theme="primary" :disabled="isRefreshing" @click="chooseShareBack">{{ landingAction }}</BaseButton>
        </template>
    </BaseDialog>

    <ShareProfileDialog
        v-if="isSharingOpen && profile && !profile.is_flagged"
        v-model:open="isSharingOpen"
        :existing-public-profile="profile"
        :available-access-levels="sharingAccessLevels"
        :org-has-ms-teams="orgHasMsTeams"
        :org-has-slack="orgHasSlack"
        :own-profile-url="ownProfileUrl"
        :recipient-name="recipientFirstName"
        share-source="share_back"
        @shared="onShared" />
    <BaseToast v-model:open="isCopiedToastOpen" :description="t('profile.collaboration.shareBack.linkCopiedToast')" :truncate-length="200" />
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { profileShareBackMessage, type ProfileShareTarget, useProfileShareActions } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { type ShareBackTarget, useProfileShareBack, withShareBackTarget } from "~vue/CollaborationProfile/composables/useProfileShareBack";
import { useProfileSharingCopy } from "~vue/CollaborationProfile/composables/useProfileSharingCopy";
import { logProfileShared } from "~vue/CollaborationProfile/profileShareAttribution";
import ProfileShareBackComparison from "~vue/CollaborationProfile/ProfileShareBackComparison.vue";
import ProfileShareBackNotification from "~vue/CollaborationProfile/ProfileShareBackNotification.vue";
import ShareProfileDialog from "~vue/CollaborationProfile/ShareProfileDialog.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import CollaborationProfileAvatar from "~vue/components/CollaborationProfileAvatar.vue";
import type { ExistingPublicProfile, ProfileCreationProps } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { ACCESS } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { firstNameOf } from "~vue/utils/stringUtils";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { sharingDisclosure } = useProfileSharingCopy();

const props = defineProps<{
    creation: ProfileCreationProps;
    profile: ExistingPublicProfile | null;
    target?: ShareBackTarget;
    pendingTarget?: ShareBackTarget | null;
    isSelf: boolean;
    linked: boolean;
    linkedByTarget?: boolean;
    preview?: boolean;
    profileReady: boolean;
    availableAccessLevels: string[];
    orgHasMsTeams: boolean;
    orgHasSlack: boolean;
}>();
const emit = defineEmits<{ finished: [] }>();
const { hasAnsweredPrompt, markPromptAnswered } = useProfileShareBack(props.creation.user_basic.uuid);
const isLinkedOpen = ref(false);
const isSharingOpen = ref(false);
const isRefreshing = ref(false);
const isCopiedToastOpen = ref(false);
const error = ref<string | null>(null);

const sharingAccessLevels = computed(() => new Set(props.availableAccessLevels));
const colleagueFallback = computed(() => t("profile.collaboration.shareBack.colleagueFallback"));
const targetFirstName = computed(() => (props.target?.name ? firstNameOf(props.target.name) : colleagueFallback.value));
// The header carries the action; the old headline drops into the body as a bold lead-in.
const landingTitle = computed(() => {
    if (props.profile?.access === ACCESS.PRIVATE) {
        return t("profile.collaboration.shareBack.enableTitle");
    }
    return props.profile ? t("profile.collaboration.shareBack.shareSubtitle") : t("profile.collaboration.shareBack.createSubtitle");
});
const landingHeadline = computed(() => {
    const name = props.target?.name || colleagueFallback.value;
    if (props.profile?.access === ACCESS.PRIVATE) {
        return t("profile.collaboration.shareBack.landing.titlePrivate", { name });
    }
    if (props.profile) {
        return t("profile.collaboration.shareBack.landing.titleUnshared", { name });
    }
    if (props.creation.user_basic.has_perspective_report) {
        return t("profile.collaboration.shareBack.landing.titleFromPerspective");
    }
    return t("profile.collaboration.shareBack.landing.titleNoProfile");
});
const landingDescription = computed(() => {
    const values = { name: targetFirstName.value };
    if (props.profile) {
        return t("profile.collaboration.shareBack.landing.descriptionShare", values);
    }
    if (props.creation.user_basic.has_perspective_report) {
        return t("profile.collaboration.shareBack.landing.descriptionCreateFromPerspective", values);
    }
    if (!props.creation.perspective_assessment_enabled) {
        return t("profile.collaboration.shareBack.landing.descriptionCreate", values);
    }
    return t("profile.collaboration.shareBack.landing.descriptionQuiz", values);
});
const landingAction = computed(() => {
    if (props.profile) {
        return t("profile.collaboration.shareBack.landing.actionShare");
    }
    if (props.creation.user_basic.has_perspective_report) {
        return t("profile.collaboration.shareBack.landing.actionCreate");
    }
    return t("profile.collaboration.shareBack.landing.actionGetStarted");
});
const recipientFirstName = computed(() => {
    const name = props.isSelf ? props.pendingTarget?.name : props.target?.name;
    return name ? firstNameOf(name) : "";
});
const shareRecipientName = computed(() => recipientFirstName.value || colleagueFallback.value);
const ownProfileUrl = computed(() => new URL(props.creation.own_profile_url, globalThis.location.origin).href);
const profileAccess = computed(() => props.profile?.access ?? ACCESS.PRIVATE);
const { copyLink, shareViaTeams } = useProfileShareActions({
    profileId: computed(() => props.profile?.id),
    access: profileAccess,
    ownProfileUrl,
    shareSource: "share_back",
});
const canPromptShareBack = computed(() => !props.isSelf && props.linked && !!props.target && !props.linkedByTarget);
const isReadyToShare = computed(() => props.isSelf && !!props.profile && !!props.pendingTarget);
const privateSharingHelp = computed(() =>
    props.availableAccessLevels.includes(ACCESS.ORGANIZATION)
        ? t("profile.collaboration.shareBack.privateHelpOrganization", { name: shareRecipientName.value })
        : t("profile.collaboration.shareBack.privateHelpInvited", { name: shareRecipientName.value }),
);
const bannerText = computed(() => {
    if (!props.profile) {
        return t("profile.collaboration.shareBack.banner.createProfile", { name: targetFirstName.value, disclosure: sharingDisclosure.value });
    }
    if (props.profile.access === ACCESS.PRIVATE) {
        return `${privateSharingHelp.value} ${sharingDisclosure.value}`;
    }
    return t("profile.collaboration.shareBack.banner.notShared", { name: targetFirstName.value, disclosure: sharingDisclosure.value });
});

watch(
    () => [canPromptShareBack.value, props.preview],
    () => {
        if (canPromptShareBack.value && props.target && (props.preview || !hasAnsweredPrompt(props.target.id))) {
            isLinkedOpen.value = true;
            void logUserInteraction(AmplitudeEventNames.PROFILE_SHARE_BACK_PROMPT_SHOWN, { has_profile: !!props.profile });
        }
    },
    { immediate: true },
);

const onLinkedOpenChange = (open: boolean) => {
    if (!open) {
        void logUserInteraction(AmplitudeEventNames.PROFILE_SHARE_BACK_DISMISSED);
    }
};
const dismissLinked = () => {
    if (props.target) {
        markPromptAnswered(props.target.id);
    }
    isLinkedOpen.value = false;
    onLinkedOpenChange(false);
};

const chooseShareBack = () => {
    if (props.target) {
        markPromptAnswered(props.target.id);
    }
    void startShareBack();
};

const startShareBack = async () => {
    if (isRefreshing.value) {
        return;
    }
    error.value = null;
    isRefreshing.value = true;
    let enabled = false;
    await new Promise<void>((resolve) =>
        router.reload({
            only: ["existing_public_profile", "viewer_public_profile", "profile_creation", "available_access_levels", "share_back_enabled", "org_has_ms_teams"],
            onSuccess: (page) => {
                enabled = page.props.share_back_enabled === true;
            },
            onFinish: () => resolve(),
        }),
    );
    try {
        if (!enabled) {
            error.value = t("profile.collaboration.shareBack.errors.sharingUnavailable");
            return;
        }
        isLinkedOpen.value = false;
        void logUserInteraction(AmplitudeEventNames.PROFILE_SHARE_BACK_CLICKED, { has_profile: !!props.profile });
        if (!props.profile && props.target) {
            void logUserInteraction(AmplitudeEventNames.PROFILE_SHARE_BACK_CREATION_STARTED, {
                has_perspective: !!props.creation.user_basic.has_perspective_report,
            });
            if (!props.creation.perspective_assessment_enabled) {
                router.visit(withShareBackTarget("/profile/create", props.target.id));
            } else if (props.creation.user_basic.has_perspective_report) {
                router.visit(withShareBackTarget("/profile/create?auto_generate=true", props.target.id));
            } else {
                router.visit(withShareBackTarget("/perspective/start/?source=profile_share_back", props.target.id));
            }
        } else if (props.profile?.is_flagged) {
            error.value = t("profile.collaboration.shareBack.errors.underReview");
        } else if (profileAccess.value === ACCESS.PRIVATE) {
            isSharingOpen.value = true;
        } else {
            const message = profileShareBackMessage(recipientFirstName.value);
            const target = props.orgHasMsTeams ? "teams" : "link";
            const shared = props.orgHasMsTeams ? await shareViaTeams(message) : await copyLink(message);
            if (shared) {
                void logProfileShared({ shareSource: "share_back", target, shareKind: "own_profile", access: profileAccess.value });
                onShared(target);
            } else {
                error.value = props.orgHasMsTeams ? t("profile.collaboration.shareBack.errors.teamsFailed") : t("profile.collaboration.shareBack.errors.copyFailed");
            }
        }
    } finally {
        isRefreshing.value = false;
    }
};

const onShared = (target: ProfileShareTarget) => {
    if (target === "link") {
        void logUserInteraction(AmplitudeEventNames.PROFILE_SHARE_BACK_COPIED);
    }
    if (props.isSelf) {
        emit("finished");
    }
    isSharingOpen.value = false;
    isCopiedToastOpen.value = target === "link";
};
</script>
